import React from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "./MobileAppDevelopment.css";

const siteUrl = "https://www.samsoftix.com";
const pageUrl = `${siteUrl}/Mobile_App_Service_SamSoftix`;
const businessId = `${siteUrl}/#business`;

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": `${siteUrl}/#website`,
      name: "SamSoftix",
      url: siteUrl,
      description:
        "SamSoftix provides web, mobile app, AI, software, ecommerce, SEO and digital solutions for clients worldwide."
    },

    {
      "@type": ["LocalBusiness", "ProfessionalService"],
      "@id": businessId,
      name: "SamSoftix",
      url: siteUrl,
      telephone: "+92-332-0472184",
      email: "info@samsoftix.com",
      description:
        "SamSoftix is a software house and training center based in Pakistan, providing mobile app development, web development, software development, AI solutions, ecommerce, SEO and digital services for clients worldwide.",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Wan",
        addressRegion: "Punjab",
        postalCode: "51010",
        addressCountry: "PK"
      },
      areaServed: [
        "Worldwide",
        "Pakistan",
        "Wan",
        "Sambrial",
        "Sialkot",
        "Punjab"
      ],
      sameAs: [
        "https://www.facebook.com/samsoftix",
        "https://www.linkedin.com/company/samsoftix",
        "https://www.instagram.com/samsoftix"
      ]
    },

    {
      "@type": "WebPage",
      "@id": `${pageUrl}#webpage`,
      url: pageUrl,
      name:
        "Mobile App Development Company | Android, iOS, Flutter & React Native | SamSoftix",
      description:
        "SamSoftix provides mobile app development services for Android, iOS, Flutter and React Native applications. Based in Pakistan and serving clients worldwide through remote development services.",
      isPartOf: {
        "@id": `${siteUrl}/#website`
      },
      about: {
        "@id": businessId
      },
      inLanguage: "en-PK"
    },

    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: siteUrl
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Mobile App Development",
          item: pageUrl
        }
      ]
    },

    {
      "@type": "Service",
      name: "Mobile App Development",
      provider: {
        "@id": businessId
      },
      serviceType: [
        "Mobile App Development",
        "Android App Development",
        "iOS App Development",
        "Flutter App Development",
        "React Native App Development",
        "Mobile UI/UX Design",
        "App Backend Development",
        "Firebase App Development"
      ],
      areaServed: "Worldwide",
      url: pageUrl,
      description:
        "Mobile application development services for businesses, startups, organizations and digital products worldwide."
    },

    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name:
            "Does SamSoftix provide mobile app development for clients worldwide?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. SamSoftix provides remote mobile app development services for businesses, startups, organizations and project owners worldwide."
          }
        },

        {
          "@type": "Question",
          name:
            "Does SamSoftix develop both Android and iOS applications?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Yes. SamSoftix can develop Android, iOS and cross-platform mobile applications depending on the project requirements."
          }
        },

        {
          "@type": "Question",
          name:
            "Which technologies are used for mobile app development?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Depending on the project, SamSoftix uses technologies such as Flutter, React Native, Android and iOS technologies, Firebase, APIs and backend services."
          }
        },

        {
          "@type": "Question",
          name:
            "How much does mobile app development cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "Mobile app development cost depends on the number of screens, features, backend requirements, integrations, design requirements and target platforms. SamSoftix provides project-specific quotations after reviewing the requirements."
          }
        },

        {
          "@type": "Question",
          name:
            "Can SamSoftix help publish an app to Google Play or the App Store?",
          acceptedAnswer: {
            "@type": "Answer",
            text:
              "SamSoftix can assist with mobile application deployment and publishing requirements for supported Google Play and Apple App Store projects."
          }
        }
      ]
    }
  ]
};

function MobileAppDevelopment() {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate("/serviceBooking");
  };

  return (
    <main className="appdev-page">
      <Helmet>
        <html lang="en-PK" />

        <title>
          Mobile App Development Company | Android, iOS, Flutter & React Native
          | SamSoftix
        </title>

        <meta
          name="description"
          content="SamSoftix provides mobile app development services for Android, iOS, Flutter and React Native applications. Based in Pakistan and serving clients worldwide through remote development."
        />

        <meta
          name="keywords"
          content="mobile app development, mobile app development company, mobile app developers, Android app development, iOS app development, Flutter app development, React Native development, mobile application development, app development company Pakistan, mobile app development Pakistan, mobile app development Sambrial, mobile app development Sialkot, worldwide mobile app development, remote mobile app development"
        />

        <meta name="author" content="SamSoftix" />

        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />

        <link rel="canonical" href={pageUrl} />

        <meta name="geo.region" content="PK-PB" />

        <meta
          name="geo.placename"
          content="Wan, Sambrial, Punjab, Pakistan"
        />

        <meta
          property="og:title"
          content="Mobile App Development Company | SamSoftix"
        />

        <meta
          property="og:description"
          content="Android, iOS, Flutter and React Native mobile app development services from SamSoftix. Based in Pakistan and serving clients worldwide."
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="SamSoftix" />

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="Mobile App Development Company | SamSoftix"
        />

        <meta
          name="twitter:description"
          content="Mobile app development for Android, iOS, Flutter and React Native applications. Serving clients worldwide."
        />

        <meta name="theme-color" content="#06111f" />

        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* =====================================================
          HERO
      ===================================================== */}

      <section className="appdev-hero">
        <div className="appdev-container">
          <span className="appdev-kicker">
            MOBILE APP DEVELOPMENT • ANDROID • IOS • FLUTTER
          </span>

          <h1>
            Mobile App Development
            <span> for Businesses Worldwide</span>
          </h1>

          <p>
            SamSoftix designs and develops mobile applications for businesses,
            startups, organizations and digital products worldwide. Based in
            Pakistan, we provide remote mobile app development services for
            clients across different markets and time zones.
          </p>

          <div className="appdev-location-tags">
            <span>Worldwide</span>
            <span>Remote Development</span>
            <span>Pakistan</span>
            <span>Sambrial</span>
            <span>Sialkot</span>
          </div>

          <div className="appdev-hero-actions">
            <button
              onClick={handleBooking}
              className="appdev-btn appdev-btn-primary"
            >
              Discuss Your App
            </button>

            <button
              onClick={handleBooking}
              className="appdev-btn appdev-btn-secondary"
            >
              Request a Quote
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          INTRO
      ===================================================== */}

      <section className="appdev-section appdev-intro-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              MOBILE SOFTWARE DEVELOPMENT
            </span>

            <h2>
              From App Idea to Production-Ready Application
            </h2>

            <p>
              We help turn business requirements and product ideas into
              functional mobile applications. Depending on the project, we can
              work on application design, frontend development, backend
              integration, authentication, databases, APIs, testing and
              deployment.
            </p>
          </div>

          <div className="appdev-feature-grid">
            <article className="appdev-feature-card">
              <span>01</span>
              <h3>Business Applications</h3>
              <p>
                Mobile solutions for businesses that need customer-facing
                applications, internal tools or digital workflows.
              </p>
            </article>

            <article className="appdev-feature-card">
              <span>02</span>
              <h3>Startup MVPs</h3>
              <p>
                Build an initial product with the core features required to
                validate an idea and develop the product further.
              </p>
            </article>

            <article className="appdev-feature-card">
              <span>03</span>
              <h3>Cross-Platform Apps</h3>
              <p>
                Develop applications for multiple platforms using technologies
                such as Flutter and React Native.
              </p>
            </article>

            <article className="appdev-feature-card">
              <span>04</span>
              <h3>Backend Integration</h3>
              <p>
                Connect applications with APIs, databases, authentication,
                cloud services and other required systems.
              </p>
            </article>

            <article className="appdev-feature-card">
              <span>05</span>
              <h3>App UI/UX</h3>
              <p>
                Create mobile interfaces designed around usability,
                navigation, responsive layouts and product requirements.
              </p>
            </article>

            <article className="appdev-feature-card">
              <span>06</span>
              <h3>Deployment Support</h3>
              <p>
                Assistance with application builds, testing and supported
                Google Play or App Store publishing requirements.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          SERVICES
      ===================================================== */}

      <section className="appdev-section appdev-services-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              OUR APP DEVELOPMENT SERVICES
            </span>

            <h2>Mobile Applications Built Around Your Requirements</h2>

            <p>
              Choose the application type and technology according to your
              product requirements, users and technical goals.
            </p>
          </div>

          <div className="appdev-services-grid">
            <article className="appdev-service-card">
              <div className="appdev-service-number">01</div>

              <h3>Android App Development</h3>

              <p>
                Android applications for consumer products, businesses,
                startups and organizations.
              </p>

              <ul>
                <li>Android application development</li>
                <li>API and database integration</li>
                <li>Authentication and user accounts</li>
              </ul>
            </article>

            <article className="appdev-service-card">
              <div className="appdev-service-number">02</div>

              <h3>iOS App Development</h3>

              <p>
                iPhone and iPad applications based on project requirements and
                supported Apple development technologies.
              </p>

              <ul>
                <li>iOS application development</li>
                <li>Mobile UI implementation</li>
                <li>App deployment support</li>
              </ul>
            </article>

            <article className="appdev-service-card">
              <div className="appdev-service-number">03</div>

              <h3>Flutter Development</h3>

              <p>
                Cross-platform applications using Flutter where a shared
                development approach fits the project.
              </p>

              <ul>
                <li>Android and iOS apps</li>
                <li>Firebase integration</li>
                <li>API and backend integration</li>
              </ul>
            </article>

            <article className="appdev-service-card">
              <div className="appdev-service-number">04</div>

              <h3>React Native Development</h3>

              <p>
                Cross-platform mobile applications using React Native for
                projects that benefit from the React ecosystem.
              </p>

              <ul>
                <li>Cross-platform development</li>
                <li>REST API integration</li>
                <li>Third-party service integration</li>
              </ul>
            </article>

            <article className="appdev-service-card">
              <div className="appdev-service-number">05</div>

              <h3>Ecommerce & Marketplace Apps</h3>

              <p>
                Mobile shopping experiences with products, customer accounts,
                orders and backend integrations.
              </p>

              <ul>
                <li>Product catalogues</li>
                <li>Cart and order workflows</li>
                <li>Backend integration</li>
              </ul>
            </article>

            <article className="appdev-service-card">
              <div className="appdev-service-number">06</div>

              <h3>Business & Management Apps</h3>

              <p>
                Custom mobile applications for business workflows,
                communication, records and internal operations.
              </p>

              <ul>
                <li>Custom dashboards</li>
                <li>User roles and permissions</li>
                <li>Business data management</li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          TECHNOLOGIES
      ===================================================== */}

      <section className="appdev-section appdev-tech-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              TECHNOLOGY STACK
            </span>

            <h2>Technologies for Modern Mobile Applications</h2>

            <p>
              Technology selection depends on application requirements,
              platform targets, integrations, maintenance needs and the
              existing project architecture.
            </p>
          </div>

          <div className="appdev-tech-grid">
            <span>Flutter</span>
            <span>React Native</span>
            <span>Firebase</span>
            <span>Node.js</span>
            <span>REST APIs</span>
            <span>Cloud Services</span>
            <span>Android</span>
            <span>iOS</span>
            <span>JavaScript</span>
            <span>TypeScript</span>
          </div>
        </div>
      </section>

      {/* =====================================================
          PROCESS
      ===================================================== */}

      <section className="appdev-section appdev-process-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              DEVELOPMENT PROCESS
            </span>

            <h2>How We Approach a Mobile App Project</h2>
          </div>

          <div className="appdev-process-grid">
            <article className="appdev-process-card">
              <span>01</span>
              <h3>Requirements</h3>
              <p>
                Understand your business, target users, required features and
                project objectives.
              </p>
            </article>

            <article className="appdev-process-card">
              <span>02</span>
              <h3>Planning & UI/UX</h3>
              <p>
                Define application structure, user flows, screens and
                technical requirements.
              </p>
            </article>

            <article className="appdev-process-card">
              <span>03</span>
              <h3>Development</h3>
              <p>
                Implement the mobile application, backend integrations and
                required functionality.
              </p>
            </article>

            <article className="appdev-process-card">
              <span>04</span>
              <h3>Testing</h3>
              <p>
                Test application workflows, interfaces, integrations and
                supported devices before release.
              </p>
            </article>

            <article className="appdev-process-card">
              <span>05</span>
              <h3>Deployment</h3>
              <p>
                Prepare the application for supported distribution platforms
                and production deployment.
              </p>
            </article>

            <article className="appdev-process-card">
              <span>06</span>
              <h3>Maintenance</h3>
              <p>
                Continue with updates, improvements and technical maintenance
                according to the agreed project scope.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          PRICING
      ===================================================== */}

      <section className="appdev-section appdev-pricing-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              PROJECT PRICING
            </span>

            <h2>Mobile App Development Cost</h2>

            <p>
              There is no single price for every mobile application. Cost
              depends on features, number of platforms, UI/UX requirements,
              backend systems, integrations and project complexity.
            </p>
          </div>

          <div className="appdev-pricing-grid">
            <article className="appdev-pricing-card">
              <span>01</span>
              <h3>MVP Application</h3>
              <p>
                Suitable for an initial product containing a defined set of
                core features.
              </p>
              <strong>Custom Quote</strong>
            </article>

            <article className="appdev-pricing-card appdev-pricing-featured">
              <span>02</span>
              <h3>Business Application</h3>
              <p>
                Applications requiring multiple screens, backend integration,
                user accounts and business workflows.
              </p>
              <strong>Custom Quote</strong>
            </article>

            <article className="appdev-pricing-card">
              <span>03</span>
              <h3>Advanced Solution</h3>
              <p>
                Larger applications involving complex workflows, integrations,
                multiple roles or specialized requirements.
              </p>
              <strong>Project Based</strong>
            </article>
          </div>

          <div className="appdev-pricing-note">
            <p>
              <strong>Need an exact estimate?</strong> Share your app
              requirements with SamSoftix and we can review the scope before
              discussing the project quotation.
            </p>

            <button
              onClick={handleBooking}
              className="appdev-btn appdev-btn-primary"
            >
              Request App Estimate
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          WORLDWIDE CLIENTS
      ===================================================== */}

      <section className="appdev-section appdev-international-section">
        <div className="appdev-container">
          <div className="appdev-international-card">
            <div>
              <span className="appdev-section-label">
                WORLDWIDE DEVELOPMENT SERVICES
              </span>

              <h2>
                Mobile App Development for Clients Worldwide
              </h2>

              <p>
                SamSoftix provides remote mobile app development services for
                businesses, startups, organizations and project owners
                worldwide. Based in Pakistan, we collaborate with clients
                remotely across different countries, regions and time zones.
              </p>

              <p>
                Whether you are launching a startup MVP, building a business
                application, creating an ecommerce app or developing a custom
                digital product, our team can work with you remotely from
                project planning through development and deployment.
              </p>
            </div>

            <div className="appdev-market-tags">
              <span>Worldwide</span>
              <span>Remote Development</span>
              <span>Pakistan</span>
              <span>International Clients</span>
              <span>Remote Collaboration</span>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          FAQ
      ===================================================== */}

      <section className="appdev-section appdev-faq-section">
        <div className="appdev-container">
          <div className="appdev-section-heading">
            <span className="appdev-section-label">
              FREQUENTLY ASKED QUESTIONS
            </span>

            <h2>Mobile App Development FAQ</h2>
          </div>

          <div className="appdev-faq-list">
            <article className="appdev-faq-item">
              <h3>
                Does SamSoftix provide mobile app development for clients
                worldwide?
              </h3>

              <p>
                Yes. SamSoftix provides remote mobile app development services
                for businesses, startups, organizations and project owners
                worldwide.
              </p>
            </article>

            <article className="appdev-faq-item">
              <h3>
                Does SamSoftix develop both Android and iOS applications?
              </h3>

              <p>
                Yes. Projects can be developed for Android, iOS or both,
                depending on the application requirements and selected
                technology.
              </p>
            </article>

            <article className="appdev-faq-item">
              <h3>
                Can you develop an app using Flutter or React Native?
              </h3>

              <p>
                Yes. Flutter and React Native can be used for cross-platform
                applications when they are appropriate for the project's
                requirements.
              </p>
            </article>

            <article className="appdev-faq-item">
              <h3>How much does mobile app development cost?</h3>

              <p>
                The cost depends on the application's features, design,
                backend, integrations, number of platforms and complexity.
                Contact SamSoftix for a project-specific estimate.
              </p>
            </article>

            <article className="appdev-faq-item">
              <h3>Can you help publish my application?</h3>

              <p>
                SamSoftix can assist with supported app deployment and
                publishing requirements for Google Play and Apple App Store
                projects.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          FINAL CTA
      ===================================================== */}

      <section className="appdev-cta">
        <div className="appdev-container">
          <div className="appdev-cta-card">
            <span className="appdev-section-label">
              HAVE AN APP IDEA?
            </span>

            <h2>Let's Discuss Your Mobile App Project</h2>

            <p>
              Tell us about your application, target users, features and
              preferred platform. SamSoftix can help you define the next step
              for your project.
            </p>

            <div className="appdev-cta-actions">
              <button
                onClick={handleBooking}
                className="appdev-btn appdev-btn-primary"
              >
                Start Your App Project
              </button>

              <a
                href="https://wa.me/923320472184"
                target="_blank"
                rel="noopener noreferrer"
                className="appdev-btn appdev-btn-secondary"
              >
                WhatsApp SamSoftix
              </a>
            </div>

            <p className="appdev-contact-line">
              Wan • Sambrial • Sialkot • Pakistan • Serving Clients Worldwide
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}

export default MobileAppDevelopment;